import ColumnSizingHelper from 'Components/Generic/AdvancedDataTable/ColumnSizingHelper';
import DataTable from 'Components/Generic/AdvancedDataTable/DataTableComponent';
import { ARRAY_TO_ID_FILTER_MAP } from 'Components/Generic/AdvancedDataTable/Helper';
import { useGlobalState } from 'Data/context/GlobalState';
import { usePersistentPaging } from 'Lib/hooks/usePersistentPaging';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import PropTypes from 'prop-types';
import { useDeferredValue, useEffect, useMemo, useRef } from 'react';
import { areEquivalent, areSetsEqual } from 'Util';

// Filter Options should be a map of potential filters to an object containing the key needed for the SeqRest call
// and a mapping function to get the SeqRest value from the filter value.  The default assumes that the filter value
// is an array of objects, and you want to map them to an array of ids.
// Example:
// {
//   status: {
//   description: {
//     seqRestKey: '*description',
//         mapToSeqRestValue: (description) => {
//       return description;
//     },
//   },
//   organization: {
//     seqRestKey: '*$transactions.parent_organization_id',
//   },
//   vendor: {
//     seqRestKey: '*$transactions.vendor_id',
//   },
// }
const AdvancedDataTable = ({
  headers,
  title,
  dataKey = 'table',
  data,
  defaultSort = 'id$DESC',
  filterOptions = {},
  filterBy,
  updateFilterField = () => {},
  highlightFunc,
  navigateToDetailsFunc,
  defaultToSmallFont = false,
  shrinkToFit = true,
  ...props
}) => {
  const [persistentPaging, setPersistentPaging] = usePersistentPaging(
    `${dataKey}_PAGING`,
    defaultSort,
  );

  const [globalState, updateGlobalState] = useGlobalState();
  const globalStateTableConfig = useMemo(
    () => globalState && globalState[`${dataKey}_TABLE_CONFIG`],
    [],
  );

  const [dataQuery, setDataQuery, queryRef] = useStateWithRef(
    globalStateTableConfig?.dataQuery,
  );
  const deferredDataQuery = useDeferredValue(dataQuery);

  const dataRef = useRef();
  dataRef.current = data;
  const deferredData = useDeferredValue(data);

  // Table configuration
  const [smallFont, setSmallFont, smallFontRef] = useStateWithRef(
    globalStateTableConfig?.smallFont || defaultToSmallFont,
  );
  const [hiddenColumns, setHiddenColumns, hiddenColumnsRef] = useStateWithRef(
    globalStateTableConfig?.hiddenColumns ||
      new Set(
        headers?.length > 0
          ? headers
              .filter((h) => h.value && (!!h.hidden || !!h.initiallyHidden))
              .map((h) => h.value)
          : [],
      ),
  );
  const initialColumnWidths = useMemo(
    () => globalStateTableConfig?.columnWidths,
    [globalStateTableConfig],
  );

  const { measureContainer, columnWidthsForTable, updateColumnWidth } =
    ColumnSizingHelper({
      headers,
      shrinkToFit,
      hiddenColumns: hiddenColumnsRef.current,
      ...(initialColumnWidths && {
        initialColumnWidths,
      }),
    });
  const columnWidthsRef = useRef();
  columnWidthsRef.current = columnWidthsForTable;

  useEffect(() => {
    return () => {
      // Update table configuration in global state when unloading
      updateGlobalState(`${dataKey}_TABLE_CONFIG`, {
        hiddenColumns: hiddenColumnsRef.current,
        smallFont: smallFontRef.current,
        columnWidths: columnWidthsRef.current,
        data: dataRef.current,
        dataQuery: queryRef.current,
      });
    };
  }, []);

  // Sizing and display of columns
  useEffect(() => {
    let newHiddenCols = new Set([...hiddenColumns]);
    headers.forEach((h) => {
      if (Object.hasOwn(h, 'hidden')) {
        if (h.hidden) {
          newHiddenCols.add(h.value);
        } else {
          newHiddenCols.delete(h.value);
        }
      }
    });
    setHiddenColumns((currentHiddenColumns) => {
      return areSetsEqual(currentHiddenColumns, newHiddenCols)
        ? currentHiddenColumns
        : newHiddenCols;
    });
  }, [headers]);

  // Updating filters
  useEffect(() => {
    let filter = filterBy;
    if (!filter) return;

    let filterWhere = {};
    Object.entries(filter).forEach(([filterName, filterValue]) => {
      let filterOption = filterOptions[filterName];

      if (filterOption) {
        filterWhere[filterOption.seqRestKey] = filterOption.mapToSeqRestValue
          ? filterOption.mapToSeqRestValue(filterValue)
          : ARRAY_TO_ID_FILTER_MAP(filterValue);
      }
    });

    if (
      (!Object.keys(filterWhere).length && !dataQuery?.where) ||
      areEquivalent(dataQuery?.where, filterWhere)
    ) {
      return;
    }

    let query = {
      ...dataQuery,
      where: filterWhere,
    };

    updateDataQuery(query);
  }, [filterBy]);

  const clearFilter = (filterName) => {
    updateFilterField(filterName, null);
  };

  const updateDataQuery = (updatedQuery) => {
    !areEquivalent(dataQuery, updatedQuery) && setDataQuery(updatedQuery);
  };

  const updateQueryWithPaging = (pagingInfo) => {
    if (!pagingInfo?.pageNumber || !pagingInfo?.pageSize) {
      return;
    }

    let sortField = pagingInfo.sort?.split('$')[0] || '';

    let sort = [
      ...(pagingInfo.sort ? [pagingInfo.sort] : []),
      ...(defaultSort && sortField !== defaultSort.split('$')[0]
        ? [defaultSort]
        : []),
    ];

    let query = {
      ...dataQuery,
      _page: pagingInfo.pageNumber,
      _pageSize: pagingInfo.pageSize,
      ...(sort?.length && { _sort: sort }),
    };

    setPersistentPaging(pagingInfo);
    updateDataQuery(query);
  };

  return (
    <DataTable
      title={title}
      data={data}
      dataQuery={dataQuery}
      persistentPaging={persistentPaging}
      onUpdatePagingInfo={updateQueryWithPaging}
      filterBy={filterBy}
      onClearFilter={clearFilter}
      headers={headers}
      hiddenColumns={hiddenColumns}
      setHiddenColumns={setHiddenColumns}
      columnWidthsForTable={columnWidthsForTable}
      updateColumnWidth={updateColumnWidth}
      measureContainer={measureContainer}
      highlightFunc={highlightFunc}
      navigateToDetailsFunc={navigateToDetailsFunc}
      smallFont={smallFont}
      setSmallFont={setSmallFont}
      {...props}
    />
  );
};

AdvancedDataTable.propTypes = {
  headers: PropTypes.array.isRequired,
  refreshKey: PropTypes.any,
  title: PropTypes.node,
  dataKey: PropTypes.string,
  data: PropTypes.shape({ records: PropTypes.array, count: PropTypes.number }),
  defaultSort: PropTypes.string,
  dataQueryFunc: PropTypes.func,
  filterOptions: PropTypes.object,
  filterBy: PropTypes.object,
  updateFilterField: PropTypes.func,
  exportDataFunc: PropTypes.func,
  actionButtons: PropTypes.node,
  children: PropTypes.node,
  autoRefreshInterval: PropTypes.number,
  highlightFunc: PropTypes.func,
  navigateToDetailsFunc: PropTypes.func,
  shrinkToFit: PropTypes.bool,
  defaultToSmallFont: PropTypes.bool,
};

export default AdvancedDataTable;
